import { Component, inject, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { DashboardService, DashboardStats } from '../../core/services/dashboard.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const MY_DATE_FORMATS = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
  },
  display: {
    dateInput: { month: '2-digit', year: 'numeric', day: '2-digit' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  template: `
    <div class="welcome-banner">
      <div class="welcome-text">
        <h1>Bienvenue, {{ authService.currentUser()?.username }} !</h1>
        <p>Voici un aperçu de l'activité de votre plateforme REP aujourd'hui.</p>
      </div>
    </div>

    <div class="dashboard-header card">
      <div class="header-controls">
        <mat-button-toggle-group [value]="scope$ | async" (change)="onScopeChange($event.value)">
          <mat-button-toggle value="global">Vue globale</mat-button-toggle>
          <mat-button-toggle value="me">Ma vue</mat-button-toggle>
        </mat-button-toggle-group>

        <form [formGroup]="dateRange" class="date-range-form">
          <mat-form-field appearance="outline">
            <mat-label>Période</mat-label>
            <mat-date-range-input [rangePicker]="picker">
              <input matStartDate formControlName="start" placeholder="Début">
              <input matEndDate formControlName="end" placeholder="Fin">
            </mat-date-range-input>
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-date-range-picker #picker></mat-date-range-picker>
          </mat-form-field>
        </form>
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon projects">
          <mat-icon aria-hidden="true">assignment</mat-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">Projets en cours</div>
          <div class="stat-value">{{ stats?.ongoingProjectsCount ?? '—' }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon fats">
          <mat-icon aria-hidden="true">fact_check</mat-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">FAT en cours</div>
          <div class="stat-value">{{ stats?.ongoingFatsCount ?? '—' }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon subcontractors">
          <mat-icon aria-hidden="true">business</mat-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">Sous-traitants</div>
          <div class="stat-value">{{ stats?.subcontractorsCount ?? '—' }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon compliant">
          <mat-icon aria-hidden="true">check_circle</mat-icon>
        </div>
        <div class="stat-info">
          <div class="stat-label">FAT Conformes</div>
          <div class="stat-value">{{ stats?.compliantFatsCount ?? '—' }}</div>
        </div>
      </div>
    </div>

    <div class="charts-container">
      <div class="chart-card">
        <h3>Projets créés par mois</h3>
        <div class="chart-wrapper">
          <canvas id="projectsChart"></canvas>
        </div>
      </div>
      <div class="chart-card">
        <h3>FAT créées par mois</h3>
        <div class="chart-wrapper">
          <canvas id="fatsChart"></canvas>
        </div>
      </div>
    </div>

    <div class="charts-container secondary">
        <div class="chart-card compliance-chart">
            <h3>Conformité des FAT</h3>
            <div class="chart-wrapper pie" [class.hidden]="isComplianceEmpty()">
                <canvas id="complianceChart"></canvas>
            </div>
            @if (isComplianceEmpty()) {
                <div class="empty-chart-message">
                    <mat-icon>info_outline</mat-icon>
                    <p>Aucune donnée de conformité pour cette période.</p>
                </div>
            }
        </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      padding: 1.5rem;
      background-color: #f8fafc;
      min-height: 100vh;
    }

    .welcome-banner {
      background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
      color: white !important;
      padding: 3rem !important;
      margin-bottom: 2rem;
      border-radius: 16px;
      position: relative;
      overflow: hidden;
      box-shadow: 0 4px 15px rgba(30, 58, 138, 0.2);
    }
    .welcome-banner::after {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 400px;
      height: 400px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      pointer-events: none;
    }
    .welcome-text h1 { margin: 0; font-size: 2.5rem; font-weight: 800; letter-spacing: -0.025em; position: relative; z-index: 1; color: white !important; }
    .welcome-text p { margin-top: 0.5rem; opacity: 0.9; font-size: 1.2rem; position: relative; z-index: 1; color: white !important; }

    .dashboard-header {
      padding: 1.25rem;
      margin-bottom: 2rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .header-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      flex-wrap: wrap;
      gap: 1.5rem;
    }
    .date-range-form {
      display: flex;
      align-items: center;
    }
    .date-range-form mat-form-field {
      width: 300px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }
    .stat-card {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      padding: 1.75rem;
      background: white;
      border-radius: 16px;
      border: 1px solid #f1f5f9;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      transition: all 0.3s ease;
    }
    .stat-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    }
    .stat-icon {
      width: 56px;
      height: 56px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.75rem;
    }
    .stat-icon.projects { background-color: #eff6ff; color: #2563eb; }
    .stat-icon.fats { background-color: #fff7ed; color: #ea580c; }
    .stat-icon.subcontractors { background-color: #f5f3ff; color: #7c3aed; }
    .stat-icon.compliant { background-color: #f0fdf4; color: #16a34a; }

    .stat-label { font-size: 0.875rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.025em; margin-bottom: 0.25rem; }
    .stat-value { font-size: 1.875rem; font-weight: 800; color: #1e293b !important; }

    .charts-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
      margin-bottom: 2rem;
    }
    .charts-container.secondary {
        grid-template-columns: 1fr;
        justify-items: center;
    }
    @media (max-width: 1024px) {
      .charts-container { grid-template-columns: 1fr; }
    }
    .chart-card {
      padding: 2.5rem;
      background: white;
      border-radius: 16px;
      border: 1px solid #f1f5f9;
      display: flex;
      flex-direction: column;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    .chart-card h3 { margin-bottom: 2rem; color: #1e293b; font-size: 1.25rem; font-weight: 700; border-left: 4px solid #3b82f6; padding-left: 1rem; }
    .chart-wrapper {
      position: relative;
      height: 320px;
      width: 100%;
    }
    .chart-wrapper.pie {
        height: 350px;
    }
    .chart-wrapper.hidden {
        visibility: hidden;
        height: 0;
    }
    
    .compliance-chart {
        width: 100%;
        max-width: 700px;
    }

    .empty-chart-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: #94a3b8;
    }
    .empty-chart-message mat-icon { font-size: 2.5rem; width: 2.5rem; height: 2.5rem; margin-bottom: 1rem; }
    .empty-chart-message p { margin: 0; font-size: 1rem; }

    .card {
      background: white;
      border-radius: 16px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      border: 1px solid #f1f5f9;
    }
  `]
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  authService = inject(AuthService);
  private dashboardService = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);

  scopeSubject = new BehaviorSubject<'global' | 'me'>('global');
  scope$ = this.scopeSubject.asObservable();

  dateRange = new FormGroup({
    start: new FormControl<Date | null>(this.getDefaultStartDate()),
    end: new FormControl<Date | null>(new Date())
  });

  stats: DashboardStats | null = null;
  private subs = new Subscription();
  private viewInitialized = false;

  private projectsChart: Chart | null = null;
  private fatsChart: Chart | null = null;
  private complianceChart: Chart | null = null;

  ngOnInit() {
    this.subs.add(
      combineLatest([
        this.scope$,
        this.dateRange.valueChanges.pipe(startWith(this.dateRange.value))
      ]).pipe(
        switchMap(([scope, dates]: ['global' | 'me', any]) => {
          const start = dates.start ? (dates.start as Date).toISOString().split('T')[0] : '';
          const end = dates.end ? (dates.end as Date).toISOString().split('T')[0] : '';
          return this.dashboardService.getDashboardStats(start, end, scope);
        })
      ).subscribe((stats: DashboardStats) => {
        console.log('Dashboard stats received:', stats);
        this.stats = stats;
        this.cdr.detectChanges();
        if (this.viewInitialized) {
          this.updateCharts();
        }
      })
    );
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    console.log('Dashboard view initialized');
    this.cdr.detectChanges();
    if (this.stats) {
      setTimeout(() => this.updateCharts(), 100);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.destroyCharts();
  }

  isComplianceEmpty(): boolean {
    return !this.stats || (this.stats.compliantFatsCount === 0 && this.stats.nonCompliantFatsCount === 0);
  }

  onScopeChange(scope: 'global' | 'me') {
    this.scopeSubject.next(scope);
  }

  private getDefaultStartDate(): Date {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  }

  private updateCharts() {
    if (!this.stats || !this.viewInitialized) return;

    // Use requestAnimationFrame to ensure DOM is ready for canvas context
    requestAnimationFrame(() => {
      this.renderProjectsChart();
      this.renderFatsChart();
      this.renderComplianceChart();
    });
  }

  private renderProjectsChart() {
    const ctx = document.getElementById('projectsChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.projectsChart) this.projectsChart.destroy();

    this.projectsChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.stats!.projectsTrend.map(d => d.label) || [],
        datasets: [{
          label: 'Projets créés',
          data: this.stats!.projectsTrend.map(d => d.value) || [],
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          borderWidth: 1,
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: '#f1f5f9' } },
          x: { grid: { display: false } }
        }
      }
    });
  }

  private renderFatsChart() {
    const ctx = document.getElementById('fatsChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.fatsChart) this.fatsChart.destroy();

    this.fatsChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.stats!.fatsTrend.map(d => d.label) || [],
        datasets: [{
          label: 'FAT créées',
          data: this.stats!.fatsTrend.map(d => d.value) || [],
          backgroundColor: 'rgba(234, 88, 12, 0.1)',
          borderColor: '#ea580c',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#ea580c'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: '#f1f5f9' } },
          x: { grid: { display: false } }
        }
      }
    });
  }

  private renderComplianceChart() {
    if (this.isComplianceEmpty()) return;

    const ctx = document.getElementById('complianceChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.complianceChart) this.complianceChart.destroy();

    this.complianceChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Conformes', 'Non conformes'],
        datasets: [{
          data: [this.stats!.compliantFatsCount, this.stats!.nonCompliantFatsCount],
          backgroundColor: ['#16a34a', '#dc2626'],
          borderColor: '#ffffff',
          borderWidth: 3,
          hoverOffset: 10
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true,
              font: { size: 13, weight: 'bold' }
            }
          }
        },
        cutout: '75%'
      }
    });
  }

  private destroyCharts() {
    if (this.projectsChart) this.projectsChart.destroy();
    if (this.fatsChart) this.fatsChart.destroy();
    if (this.complianceChart) this.complianceChart.destroy();
  }
}
